// 4. 10. 2026 (package 3, F: after level 20, 7.g of docs/ANALIZA_slicne_igre.md): a Pristine affix. A survivor on
// level 20 finding gear in a zone of danger 5 has a 4% chance (a Legendary 15%) that one of the item's affixes
// rolls 1.3 times the most the catalog gives that affix on that item. Its value is the item's, so it counts in
// Power; a star marks the card and the report makes the find its key moment. `on: false` (simulations): the rules
// before.

import { ZONES } from '../content/zones';
import { affixRange, MAX_DURABILITY, roundAffix } from '../itemgen';
import type { Rng } from '../rng';
import type { ItemInstance, ItemStatKey, Rarity, ZoneId } from '../types';

// 4. 10. 2026 (owner, after the final pairs of part 0): 4% / 15% gave about one a day on level 20; now about 2 a week for
// the heavy profile and 1 for the actives (sim:progress, BALANCE_REPORT package 3); a Legendary keeps its 3.75x.
export const PRISTINE_RULES = { on: true, minLevel: 20, danger: 5, chance: 0.008, legendaryChance: 0.03, mult: 1.3 };

/** The chance that gear found by a survivor of `level` in `zone` comes with a Pristine affix (0 when it cannot). */
export function pristineChance(level: number, zone: ZoneId, rarity: Rarity): number {
  if (!PRISTINE_RULES.on || level < PRISTINE_RULES.minLevel || (ZONES[zone]?.danger ?? 0) < PRISTINE_RULES.danger) return 0;
  return rarity === 'legendary' ? PRISTINE_RULES.legendaryChance : PRISTINE_RULES.chance;
}

export const isPristine = (item: Pick<ItemInstance, 'affixes'>): boolean => item.affixes.some((a) => a.pristine);

/** A Pristine affix's value on this item: 1.3× the most the catalog lets it roll here (null: no catalog range). */
export function pristineValue(item: Pick<ItemInstance, 'templateId' | 'ilvl' | 'rarity' | 'tier'>, stat: ItemStatKey): number | null {
  const r = affixRange(item.templateId, stat, item.ilvl, item.rarity, item.tier);
  return r ? roundAffix(r[1] * PRISTINE_RULES.mult, stat) : null;
}

/**
 * The item with one affix made Pristine (the dice pick which, among those with a catalog range). An item that already
 * has one, or has none that can be, stays as it is. A Pristine durability affix makes the item sturdier, full.
 */
export function makePristine<T extends ItemInstance>(item: T, rng: Rng): T {
  if (isPristine(item)) return item;
  const can = item.affixes.map((a, i) => ({ i, stat: a.stat, value: pristineValue(item, a.stat) })).filter((x) => x.value != null);
  if (!can.length) return item;
  const pick = rng.pick(can);
  const affixes = item.affixes.map((a, i) => (i === pick.i ? { ...a, value: Math.max(a.value, pick.value!), pristine: true } : a));
  if (pick.stat !== 'durability') return { ...item, affixes };
  const maxDurability = Math.round(MAX_DURABILITY * (1 + (affixes[pick.i].value ?? 0)));
  return { ...item, affixes, maxDurability, durability: maxDurability };
}
